import React, { useState, useCallback } from 'react';
import { Page, PageHead, PageTitle, PageSub } from '../components/ui/Page';
import { Card } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { Kicker } from '../components/ui/Kicker';

const calcVolumetric = (l: number, w: number, h: number): number =>
  Math.round(((l * w * h) / 5000) * 100) / 100;

const VolumetricWeightPage: React.FC = () => {
  const [dims, setDims] = useState({ l: 30, w: 20, h: 10, actual: 2.5 });

  const set = useCallback((key: keyof typeof dims, val: string) => {
    const n = parseFloat(val);
    if (!isNaN(n) && n >= 0) setDims((prev) => ({ ...prev, [key]: n }));
  }, []);

  const vol = calcVolumetric(dims.l, dims.w, dims.h);
  const billable = Math.max(vol, dims.actual);

  return (
    <Page width="narrow">
      <PageHead>
        <div>
          <Kicker>Pricing</Kicker>
          <PageTitle style={{ marginTop: 8 }}>Volumetric weight, explained</PageTitle>
          <PageSub>
            Carriers bill by whichever is higher: actual weight or volumetric weight.
          </PageSub>
        </div>
      </PageHead>

      <Card style={{ padding: 28, marginTop: 16 }}>
        <div
          style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, alignItems: 'center' }}
          className="details-grid"
        >
          {/* Formula */}
          <div>
            <h3>Formula</h3>
            <Card style={{ background: 'var(--bg-tint)', marginTop: 10, textAlign: 'center', padding: 24 }}>
              <div className="mono" style={{ fontSize: 20, fontWeight: 600 }}>(L × W × H) ÷ 5000</div>
              <div className="muted" style={{ fontSize: 13, marginTop: 6 }}>cm ÷ 5000 = kg</div>
            </Card>
            <p className="muted" style={{ marginTop: 14, fontSize: 14, lineHeight: 1.6 }}>
              Air carriers use divisor 5000. Ground typically 6000. You always pay the higher of
              actual vs. volumetric.
            </p>

            <h3 style={{ marginTop: 24 }}>How carriers decide</h3>
            <div style={{ marginTop: 12, fontSize: 14, lineHeight: 1.7, color: 'var(--ink-2)' }}>
              <p>
                When a large but light box ships, the carrier loses revenue because the item occupies
                space that could hold heavier cargo. Volumetric weight corrects for this by pricing
                based on volume.
              </p>
              <p style={{ marginTop: 8 }}>
                The divisor varies by carrier and mode — air freight uses 5000, road freight uses
                4000–6000. Always verify with your specific carrier.
              </p>
            </div>
          </div>

          {/* Calculator */}
          <div>
            <h3>Calculator</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 10 }}>
              <div className="field">
                <label className="field-label">L (cm)</label>
                <Input
                  type="number"
                  defaultValue={dims.l}
                  onChange={(e) => set('l', e.target.value)}
                  min={0}
                />
              </div>
              <div className="field">
                <label className="field-label">W (cm)</label>
                <Input
                  type="number"
                  defaultValue={dims.w}
                  onChange={(e) => set('w', e.target.value)}
                  min={0}
                />
              </div>
              <div className="field">
                <label className="field-label">H (cm)</label>
                <Input
                  type="number"
                  defaultValue={dims.h}
                  onChange={(e) => set('h', e.target.value)}
                  min={0}
                />
              </div>
              <div className="field">
                <label className="field-label">Actual wt (kg)</label>
                <Input
                  type="number"
                  defaultValue={dims.actual}
                  onChange={(e) => set('actual', e.target.value)}
                  min={0}
                  step={0.1}
                />
              </div>
            </div>

            <div
              style={{
                marginTop: 14,
                padding: 16,
                background: 'var(--bg-tint)',
                borderRadius: 10,
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14 }}>
                <span className="muted">Volumetric</span>
                <span className="mono" style={{ fontWeight: 600 }}>{vol.toFixed(2)} kg</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, marginTop: 6 }}>
                <span className="muted">Actual</span>
                <span className="mono" style={{ fontWeight: 600 }}>{dims.actual.toFixed(2)} kg</span>
              </div>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: 16,
                  marginTop: 10,
                  paddingTop: 10,
                  borderTop: '1px solid var(--line-strong)',
                }}
              >
                <span style={{ fontWeight: 600 }}>Billable</span>
                <span className="mono" style={{ fontWeight: 700, color: 'var(--brand-blue)' }}>
                  {billable.toFixed(2)} kg
                </span>
              </div>
            </div>

            <p className="muted" style={{ fontSize: 12, marginTop: 10 }}>
              {billable === vol && vol > dims.actual
                ? 'Volumetric weight applies — your package is light for its size.'
                : 'Actual weight applies — your package is dense enough.'}
            </p>
          </div>
        </div>
      </Card>
    </Page>
  );
};

export default VolumetricWeightPage;
